import { ClipLoader } from "react-spinners";
import { transact, tx, useAuth, useQuery } from "./db.ts";
import {
  ErrorMessage,
  InvalidIdError,
  isUuid,
  Loading,
} from "./uiComponents.tsx";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "preact/hooks";
import { useLocation } from "preact-iso";
import { parseAndModifyHtml } from "./conversation.tsx";
import { Pause, Play, RotateCcw } from "lucide-preact";
import "./style.css";
import {
  participantColor,
  primaryColor,
  resetParticipantColors,
  t,
} from "./theme.ts";
import { normalizeTimestamp } from "../../src/timeUtils.ts";

type SnapshotMessage = {
  id: string;
  from: string;
  text: string;
  time: number;
  // deno-lint-ignore no-explicit-any
  [key: string]: any;
};

// Strip <pre> and <code> elements from HTML string and replace with placeholder.
const stripCodeBlocks = (html: string): string => {
  const placeholder =
    `<pre style="opacity: 0.5"><code>Some code...</code></pre>`;
  if (typeof document === "undefined") {
    // Regex fallback (less accurate but needed for SSR)
    let processed = html.replace(/<pre[\s>][\s\S]*?<\/pre>/gi, placeholder);
    // Only replace <code> if it wasn't inside a <pre> (already replaced)
    processed = processed.replace(/<code[\s>][\s\S]*?<\/code>/gi, placeholder);
    return processed;
  }
  const tmp = document.createElement("div");
  tmp.innerHTML = html;

  // Replace <pre> blocks first
  tmp.querySelectorAll("pre").forEach((el) => {
    el.outerHTML = placeholder;
  });

  // Replace remaining <code> blocks (inline code)
  tmp.querySelectorAll("code").forEach((el) => {
    // Make sure we didn't already replace its parent
    if (!el.closest("pre")) {
      el.outerHTML = `<code style="opacity: 0.5">some code...</code>`;
    }
  });

  return tmp.innerHTML;
};

// Count visible text characters in an HTML string (excludes tags).
const countTextChars = (html: string): number => {
  if (typeof document === "undefined") {
    return html.replace(/<[^>]*>/g, "").length;
  }
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || "").length;
};

// Format elapsed time relative to the first event
const formatElapsed = (ms: number): string => {
  if (ms <= 0) return "start";
  const sec = Math.floor(ms / 1000);
  const min = Math.floor(sec / 60);
  const hrs = Math.floor(min / 60);
  const d = Math.floor(hrs / 24);

  if (d > 0) {
    const h = hrs % 24;
    return h > 0 ? `+${d}d ${h}h` : `+${d}d`;
  }
  if (hrs > 0) {
    const m = min % 60;
    return m > 0 ? `+${hrs}h ${m}m` : `+${hrs}h`;
  }
  if (min > 0) {
    const s = sec % 60;
    return s > 0 ? `+${min}m ${s}s` : `+${min}m`;
  }
  return sec > 0 ? `+${sec}s` : "start";
};

// Truncate an HTML string to `limit` visible text characters, preserving all
// HTML structure (tags, attributes, nesting). Works by cloning the DOM tree and
// trimming text nodes once the budget is exhausted.
const truncateHtmlToChars = (html: string, limit: number): string => {
  if (typeof document === "undefined") return html;
  const container = document.createElement("div");
  container.innerHTML = html;

  let remaining = limit;

  const walk = (node: Node): boolean => {
    if (remaining <= 0) {
      node.parentNode?.removeChild(node);
      return false;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || "";
      if (text.length <= remaining) {
        remaining -= text.length;
      } else {
        node.textContent = text.slice(0, remaining);
        remaining = 0;
      }
      return true;
    }
    // Element node — walk children (snapshot array first since we mutate)
    const children = Array.from(node.childNodes);
    for (const child of children) {
      walk(child);
    }
    return true;
  };

  const children = Array.from(container.childNodes);
  for (const child of children) {
    walk(child);
  }

  return container.innerHTML;
};

const TypingBubble = ({
  html,
  visibleChars,
  totalChars,
  align,
  from,
}: {
  html: string;
  visibleChars: number;
  totalChars: number;
  align: "flex-start" | "flex-end";
  from: string;
}) => {
  const color = participantColor(from);
  const bubbleBg = color.bg;
  const bubbleText = color.text;
  const isLeft = align === "flex-start";
  const displayedHtml = visibleChars >= totalChars
    ? html
    : truncateHtmlToChars(html, visibleChars);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isLeft ? "row" : "row-reverse",
        alignItems: "flex-end",
        gap: 4,
        alignSelf: align,
        maxWidth: "85%",
      }}
    >
      <div
        style={{
          flexDirection: "column",
          alignItems: align,
          backgroundColor: bubbleBg,
          color: bubbleText,
          borderRadius: 18,
          borderTopLeftRadius: isLeft ? 4 : 18,
          borderTopRightRadius: isLeft ? 18 : 4,
          minWidth: 40,
          boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
          display: "flex",
          position: "relative",
          overflow: "hidden",
          flex: "0 1 auto",
          minHeight: 0,
        }}
      >
        <div
          style={{
            position: "relative",
            flex: 1,
            minWidth: 0,
          }}
        >
          <div
            dir="auto"
            className="chat-message"
            style={{
              padding: "10px 16px",
              whiteSpace: "pre-wrap",
              overflowWrap: "break-word",
              wordBreak: "break-word",
              unicodeBidi: "plaintext",
              textAlign: "start",
              overflowX: "auto",
              overflowY: "hidden",
              fontSize: 15,
              lineHeight: 1.5,
            }}
          >
            <span dangerouslySetInnerHTML={{ __html: displayedHtml }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export const SnapshotPage = ({ id }: { id: string }) => {
  if (!isUuid(id)) {
    return <InvalidIdError id={id} type="snapshot" />;
  }
  return <SnapshotPageInner id={id} />;
};

const SnapshotPageInner = ({ id }: { id: string }) => {
  const th = t();
  const { user } = useAuth();
  const { isLoading, data, error } = useQuery({
    snapshots: { $: { where: { id } }, account: { admins: {} } },
  });

  if (isLoading) {
    return (
      <div
        style={{
          height: "100vh",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: th.pageBg,
        }}
      >
        <ClipLoader color={primaryColor()} />
      </div>
    );
  }
  if (error) return <ErrorMessage error={error} />;

  const snapshot = data?.snapshots?.[0];
  if (!snapshot) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: th.textMuted,
          background: th.pageBg,
        }}
      >
        Snapshot not found
      </div>
    );
  }

  // deno-lint-ignore no-explicit-any
  const account = (snapshot as any).account;
  const admins: { email: string }[] = account?.admins ?? [];
  const canDelete = !!user?.email && admins.some((a) => a.email === user.email);

  let messages: SnapshotMessage[] = [];
  try {
    messages = JSON.parse(snapshot.messagesJson || "[]");
    messages.sort((a, b) =>
      normalizeTimestamp(a.time) - normalizeTimestamp(b.time)
    );
  } catch (e) {
    console.error("Failed to parse snapshot messages", e);
  }

  return (
    <SnapshotPlayer messages={messages} snapshotId={id} canDelete={canDelete} />
  );
};

const SnapshotPlayer = (
  { messages, snapshotId, canDelete }: {
    messages: SnapshotMessage[];
    snapshotId: string;
    canDelete: boolean;
  },
) => {
  const th = t();
  const location = useLocation();

  // "Hide code blocks" — persisted via URL param
  const initialHideCode =
    new URLSearchParams(globalThis.location?.search).get("hideCode") === "1";
  const [hideCode, setHideCode] = useState(initialHideCode);

  const toggleHideCode = useCallback(() => {
    setHideCode((prev) => {
      const next = !prev;
      const url = new URL(globalThis.location.href);
      if (next) url.searchParams.set("hideCode", "1");
      else url.searchParams.delete("hideCode");
      globalThis.history.replaceState(null, "", url.toString());
      return next;
    });
  }, []);

  // State: which message index we're typing, how many chars shown in current message
  const [currentMsgIndex, setCurrentMsgIndex] = useState(0);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true); // auto-start
  const [speedMultiplier, setSpeedMultiplier] = useState(2);
  const [isFinished, setIsFinished] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Track whether user is scrolled near the bottom
  const isNearBottomRef = useRef(true);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const el = chatContainerRef.current;
    if (!el) return;
    isNearBottomRef.current =
      el.scrollHeight - (el.scrollTop + el.clientHeight) < 80;
  }, []);

  useEffect(() => {
    const el = chatContainerRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleDelete = async () => {
    if (!confirm("Delete this snapshot? This cannot be undone.")) return;
    setIsDeleting(true);
    try {
      await transact(tx.snapshots[snapshotId].delete());
      location.route("/");
    } catch (e) {
      console.error("Failed to delete snapshot", e);
      setIsDeleting(false);
    }
  };

  // Pre-process messages: sanitize via parseAndModifyHtml (linkify, scrub, etc.),
  // and optionally strip code blocks. This matches the chat view's pipeline so
  // code blocks render with proper styling instead of as raw inline text.
  const processedTexts = useMemo(
    () =>
      messages.map((m) => {
        const sanitized = parseAndModifyHtml(m.text);
        return hideCode ? stripCodeBlocks(sanitized) : sanitized;
      }),
    [messages, hideCode],
  );

  // Pre-compute text char count for each message (counts visible chars, not HTML tags)
  const charCounts = useMemo(
    () => processedTexts.map((t) => countTextChars(t)),
    [processedTexts],
  );

  // Reset participant color cache when this snapshot mounts/changes so that
  // colors are assigned in first-appearance order, identical to the chat view.
  useEffect(() => {
    resetParticipantColors();
  }, [snapshotId]);

  // Reset playback position when hideCode changes (char counts differ)
  useEffect(() => {
    setCurrentMsgIndex(0);
    setCurrentCharIndex(0);
    setIsFinished(false);
  }, [hideCode]);

  // Auto-scroll to bottom only when user is already near the bottom
  useLayoutEffect(() => {
    const el = chatContainerRef.current;
    if (isNearBottomRef.current && el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [currentMsgIndex, currentCharIndex]);

  // Typewriter tick
  useEffect(() => {
    if (!isPlaying || messages.length === 0) return;

    if (currentMsgIndex >= messages.length) {
      setIsPlaying(false);
      setIsFinished(true);
      return;
    }

    const msgCharCount = charCounts[currentMsgIndex];

    if (currentCharIndex < msgCharCount) {
      // Type next character
      const baseDelay = 18; // ms per character
      const delay = baseDelay / speedMultiplier;
      const timeout = setTimeout(() => {
        // Type multiple chars at once for speed > 2x
        const charsPerTick = speedMultiplier >= 4
          ? 4
          : speedMultiplier >= 2
          ? 2
          : 1;
        setCurrentCharIndex((c) => Math.min(c + charsPerTick, msgCharCount));
      }, delay);
      return () => clearTimeout(timeout);
    } else {
      // Message fully typed — pause then move to next
      const pauseDelay = Math.min(400, 600 / speedMultiplier);
      const timeout = setTimeout(() => {
        setCurrentMsgIndex((i) => i + 1);
        setCurrentCharIndex(0);
      }, pauseDelay);
      return () => clearTimeout(timeout);
    }
  }, [
    isPlaying,
    currentMsgIndex,
    currentCharIndex,
    messages,
    speedMultiplier,
    charCounts,
  ]);

  const handlePlayPause = useCallback(() => {
    if (isFinished) {
      // Restart
      setCurrentMsgIndex(0);
      setCurrentCharIndex(0);
      setIsFinished(false);
      setIsPlaying(true);
    } else {
      setIsPlaying((p) => !p);
    }
  }, [isFinished]);

  // Calculate scrubber position (0 to total chars across all messages)
  const totalCharsAll = charCounts.reduce((s, c) => s + c, 0);
  const typedChars = charCounts
    .slice(0, currentMsgIndex)
    .reduce((s, c) => s + c, 0) + currentCharIndex;

  const seekTo = useCallback((targetChar: number) => {
    targetChar = Math.max(0, Math.min(targetChar, totalCharsAll));
    setIsPlaying(false);
    setIsFinished(false);

    let remaining = targetChar;
    for (let i = 0; i < messages.length; i++) {
      const len = charCounts[i];
      if (remaining <= len) {
        setCurrentMsgIndex(i);
        setCurrentCharIndex(remaining);
        return;
      }
      remaining -= len;
    }
    setCurrentMsgIndex(messages.length);
    setCurrentCharIndex(0);
    setIsFinished(true);
  }, [totalCharsAll, messages.length, charCounts]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        if (e.target.type !== "range") return;
      }
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        // Jump by 2% of the timeline or 10 chars, whichever is larger
        const step = Math.max(10, Math.floor(totalCharsAll * 0.02));
        const direction = e.key === "ArrowLeft" ? -1 : 1;
        seekTo(typedChars + direction * step);
        e.preventDefault();
      } else if (e.key === " ") {
        handlePlayPause();
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [totalCharsAll, typedChars, seekTo, handlePlayPause]);

  const handleScrub = (e: Event) => {
    const target = e.currentTarget as HTMLInputElement;
    seekTo(Number(target.value));
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: th.pageBg,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          flexShrink: 0,
          background: th.surfaceBg,
          borderBottom: `1px solid ${th.border}`,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 20px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
              userSelect: "none",
            }}
            onClick={() => location.route("/")}
          >
            <img
              src="/favicon.svg"
              alt="ViewChat Logo"
              style={{ width: 24, height: 24 }}
            />
            <span
              style={{
                color: primaryColor(),
                fontWeight: 700,
                fontSize: 22,
                letterSpacing: 1,
              }}
            >
              ViewChat
            </span>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                color: th.textMuted,
                fontSize: 13,
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <input
                type="checkbox"
                checked={hideCode}
                onChange={toggleHideCode}
                style={{ cursor: "pointer" }}
              />
              Hide code
            </label>
            {canDelete && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="compact-button"
                style={{
                  padding: "6px 14px",
                  background: th.dangerBg,
                  color: "#ff8888",
                  border: `1px solid ${th.dangerBorder}`,
                }}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable chat area */}
      <div
        ref={chatContainerRef}
        style={{
          flex: 1,
          overflowY: "auto",
          scrollbarGutter: "stable",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            padding: 16,
            maxWidth: 900,
            width: "100%",
            margin: "0 auto",
            boxSizing: "border-box",
          }}
        >
          {(() => {
            let currentSide: "flex-end" | "flex-start" = "flex-end";
            let previousSpeaker: string | null = null;
            return messages.slice(0, currentMsgIndex + 1).map((msg, i) => {
              if (previousSpeaker !== null && previousSpeaker !== msg.from) {
                currentSide = currentSide === "flex-start"
                  ? "flex-end"
                  : "flex-start";
              }
              previousSpeaker = msg.from;
              const align = currentSide;
              const color = participantColor(msg.from);
              const isCurrentMsg = i === currentMsgIndex;
              const visibleChars = isCurrentMsg
                ? currentCharIndex
                : charCounts[i]; // fully typed

              return (
                <div
                  key={msg.id || i}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: align,
                    marginBottom: 4,
                    width: "100%",
                    animation: i === currentMsgIndex && currentCharIndex === 0
                      ? "snapshot-fade-in 0.2s ease-out"
                      : undefined,
                  }}
                >
                  <div
                    style={{
                      color: color.label,
                      fontWeight: "bold",
                      marginBottom: 3,
                      fontSize: 13,
                      alignSelf: align,
                      opacity: 0.98,
                      letterSpacing: 0.1,
                    }}
                  >
                    {msg.from}
                  </div>
                  <TypingBubble
                    html={processedTexts[i]}
                    visibleChars={visibleChars}
                    totalChars={charCounts[i]}
                    align={align}
                    from={msg.from}
                  />
                  {visibleChars >= charCounts[i] && (
                    <div
                      title={normalizeTimestamp(msg.time) > 0
                        ? new Date(normalizeTimestamp(msg.time)).toString()
                        : ""}
                      style={{
                        fontSize: 11,
                        color: th.textMuted,
                        marginTop: 2,
                        alignSelf: align,
                      }}
                    >
                      {formatElapsed(
                        normalizeTimestamp(msg.time) -
                          normalizeTimestamp(messages[0].time),
                      )}
                    </div>
                  )}
                </div>
              );
            });
          })()}
          {isFinished && (
            <div
              style={{
                textAlign: "center",
                color: th.textFaint,
                fontSize: 13,
                padding: "20px 0",
              }}
            >
              End of conversation
            </div>
          )}
        </div>
      </div>

      {/* Bottom controls */}
      <div
        style={{
          flexShrink: 0,
          background: th.surfaceBg,
          borderTop: `1px solid ${th.border}`,
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 20px",
        }}
      >
        <button
          type="button"
          onClick={handlePlayPause}
          className="compact-button"
          style={{
            padding: "6px 8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          title={isFinished ? "Replay" : isPlaying ? "Pause" : "Play"}
        >
          {isFinished
            ? <RotateCcw size={18} />
            : isPlaying
            ? <Pause size={18} />
            : <Play size={18} />}
        </button>
        <input
          type="range"
          min={0}
          max={totalCharsAll}
          value={typedChars}
          onInput={handleScrub}
          style={{ flex: 1 }}
        />
        <select
          value={speedMultiplier}
          onChange={(e) => setSpeedMultiplier(Number(e.currentTarget.value))}
          style={{
            padding: "6px 8px",
            borderRadius: 6,
            background: th.inputBg,
            color: th.text,
            border: `1px solid ${th.inputBorder}`,
            fontSize: 14,
          }}
        >
          <option value={0.5}>0.5x</option>
          <option value={1}>1x</option>
          <option value={2}>2x</option>
          <option value={4}>4x</option>
          <option value={8}>8x</option>
        </select>
        <span
          style={{
            color: th.textDim,
            fontSize: 13,
            minWidth: 50,
            textAlign: "right",
          }}
        >
          {currentMsgIndex >= messages.length
            ? messages.length
            : currentMsgIndex + 1}
          {" / "}
          {messages.length}
        </span>
      </div>
    </div>
  );
};
